import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import * as XLSX from "xlsx";

function fmt(ts: string | null) {
  if (!ts) return "";
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

export default function BMAuditExportButton() {
  const { hasRole } = useAuth();
  const [loading, setLoading] = useState(false);

  if (!hasRole(["admin", "superadmin"])) return null;

  const handleExport = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("bm-audit-export");
      if (error) throw error;
      const { fundHouses = [], holdings = [], mappings = [], snapshots = [], auditLog = [] } = data || {};

      const houseMap = new Map<string, any>(fundHouses.map((h: any) => [h.id, h]));
      const holdingMap = new Map<string, any>(holdings.map((h: any) => [h.id, h]));

      const wb = XLSX.utils.book_new();

      // 1. Audit Log (going-forward, every change)
      const auditRows = auditLog.map((a: any) => ({
        "Timestamp": fmt(a.changed_at),
        "Table": a.table_name,
        "Action": a.action,
        "Row ID": a.row_id,
        "User Email": a.user_email || "(system / unknown)",
        "User ID": a.user_id || "",
        "Before": a.before_data ? JSON.stringify(a.before_data) : "",
        "After": a.after_data ? JSON.stringify(a.after_data) : "",
      }));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(auditRows), "Audit Log");

      // 1b. Sector Mapping Changes (filtered + resolved names)
      const smRows = auditLog
        .filter((a: any) => a.table_name === "bm_holding_sector_mappings")
        .map((a: any) => {
          const payload = a.after_data || a.before_data || {};
          const holdingId = payload.holding_id;
          const hld = holdingId ? holdingMap.get(holdingId) : null;
          const fh = hld ? houseMap.get(hld.fund_house_id) : null;
          const before = a.before_data || {};
          const after = a.after_data || {};
          const sectorBefore = before.cbam_sector_name ?? "";
          const sectorAfter = after.cbam_sector_name ?? "";
          const weightBefore = before.weight_pct ?? "";
          const weightAfter = after.weight_pct ?? "";
          return {
            "Timestamp": fmt(a.changed_at),
            "Action": a.action,
            "User Email": a.user_email || "(system / unknown)",
            "Fund House": fh?.name || "",
            "Benchmark": fh?.benchmark ?? "",
            "Holding": hld?.holding_name || "",
            "Sector (Before)": sectorBefore,
            "Sector (After)": sectorAfter,
            "Weight % (Before)": weightBefore,
            "Weight % (After)": weightAfter,
            "Holding ID": holdingId || "",
            "Row ID": a.row_id,
          };
        });
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(smRows), "Sector Mapping Changes");

      // 2. Holdings Snapshots (summary)
      const snapRows = snapshots.map((s: any) => {
        const fh = houseMap.get(s.fund_house_id);
        return {
          "Snapshot At": fmt(s.snapshot_at),
          "Fund House": fh?.name || "",
          "Benchmark": fh?.benchmark || "",
          "Reason": s.reason,
          "Source File": s.source_filename || "",
          "Drive URL": s.drive_file_url || "",
          "User Email": s.user_email || "(system / manual edit pre-audit)",
          "Holdings Count": Array.isArray(s.holdings) ? s.holdings.length : 0,
        };
      });
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(snapRows), "Holdings Snapshots");

      // 3. Snapshot Holdings (flat)
      const snapHoldRows: any[] = [];
      snapshots.forEach((s: any) => {
        const fh = houseMap.get(s.fund_house_id);
        (s.holdings || []).forEach((h: any) => {
          snapHoldRows.push({
            "Snapshot At": fmt(s.snapshot_at),
            "Fund House": fh?.name || "",
            "Benchmark": fh?.benchmark || "",
            "User Email": s.user_email || "",
            "Holding": h.holding_name,
            "Weight %": h.holding_weight_pct,
            "Sort Order": h.sort_order,
          });
        });
      });
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(snapHoldRows), "Snapshot Holdings");

      // 4. Current Fund Houses
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(fundHouses.map((h: any) => ({
        "Fund House": h.name,
        "Benchmark": h.benchmark,
        "Sort Order": h.sort_order,
        "Factsheet As Of": h.factsheet_as_of || "",
        "Created At": fmt(h.created_at),
        "Updated At": fmt(h.updated_at),
        "ID": h.id,
      }))), "Current Fund Houses");

      // 5. Current Holdings
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(holdings.map((h: any) => {
        const fh = houseMap.get(h.fund_house_id);
        return {
          "Fund House": fh?.name || "",
          "Benchmark": fh?.benchmark || "",
          "Holding": h.holding_name,
          "Weight %": h.holding_weight_pct,
          "Sort Order": h.sort_order,
          "Created At": fmt(h.created_at),
          "Updated At": fmt(h.updated_at),
          "ID": h.id,
        };
      })), "Current Holdings");

      // 6. Current Sector Mappings
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(mappings.map((m: any) => {
        const hld = holdingMap.get(m.holding_id);
        const fh = hld ? houseMap.get(hld.fund_house_id) : null;
        return {
          "Fund House": fh?.name || "",
          "Benchmark": fh?.benchmark || "",
          "Holding": hld?.holding_name || "",
          "CBAM Sector": m.cbam_sector_name,
          "Weight %": m.weight_pct,
          "Created At": fmt(m.created_at),
          "Updated At": fmt(m.updated_at),
          "Holding ID": m.holding_id,
        };
      })), "Current Sector Mappings");

      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      XLSX.writeFile(wb, `bm-audit-export-${ts}.xlsx`);
      toast.success("Export downloaded");
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || "Export failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button size="sm" variant="outline" onClick={handleExport} disabled={loading}>
      {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
      Export Change History (XLSX)
    </Button>
  );
}
